import { useSuspenseQuery } from "@tanstack/react-query";
import {
  useNavigate,
  useParams,
  createFileRoute,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { getProcessTasks, processStartRequest } from "../../api/processRequest";
import { getStartForm } from "../../api/tasks";
import FormRenderer, {
  FormRendererProps,
} from "../../pages/protected/components/FormRenderer";
import LoaderWithDescription from "../../components/LoaderWithDescription";
import { notify, notifyWithTitle } from "../../pages/public/components/Toast";
import extractInnerMessage, { extractApiScriptErrorMessage } from "../../utils/apiErrorUtils";

type Variables = Record<
  string,
  {
    type: string;
    value: string;
    valueInfo: object;
  }
>;

interface StartFormObject {
  form: [];
  variables: Variables;
  conclusionButtonText: "defaultLabel";
  isSignatureTask: false;
}

const queryOpts = (processId: string) => ({
  queryKey: ["start-form", processId],
  queryFn: async () =>
    (await getStartForm(processId)) as unknown as StartFormObject,
  retry: false,
});

export const Route = createFileRoute("/__auth/first-registration/$processId")({
  component: FirstRegistration,
  pendingComponent: () => (
    <LoaderWithDescription
      title="Iniciando a solicitação"
      subtitle="Por favor, aguarde enquanto o formulário é carregado"
    />
  ),
  loader: async ({ context: { queryClient }, params: { processId } }) => {
    const data = await queryClient.ensureQueryData(queryOpts(processId));
    await new Promise((resolve) => setTimeout(resolve, 1500));
    return { ...data };
  },
});

function FirstRegistration(): React.JSX.Element {
  const navigate = useNavigate();
  const params = useParams({
    from: "/__auth/first-registration/$processId",
  }) as { processId: string };
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { data, error } = useSuspenseQuery(queryOpts(params.processId));
  useEffect(() => {
    if (error) {
      console.error(error)
      navigate({ to: "/error" });
    }
  }, [error, navigate]);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const callback: FormRendererProps["callback"] = async (values, processId, _, reloadFormView) => {
    setIsInitialLoad(false);
    try {
      const process = await processStartRequest(values, processId);
      const task = await getProcessTasks(process.data.id);

      let pathTask = "task";
      if (task.data[0]) {
        if (task.data[0].isSignatureTask) {
          pathTask = "signature";
        }

        void navigate({
          to: `/${pathTask}/${process.data.id}/${task.data[0].id}` as any,
        });
      } else {
        void navigate({ to: `/` });
      }
    } catch (error) {
      const apiErrorMessage = extractInnerMessage(error);

      console.error("error start process:", apiErrorMessage);

      const scriptErrorMessage = extractApiScriptErrorMessage(apiErrorMessage);

      const isCustomScriptError = apiErrorMessage !== scriptErrorMessage;

      if(isCustomScriptError){
        notifyWithTitle(scriptErrorMessage, apiErrorMessage, "error");
      } else{
        notify(apiErrorMessage, "error", "", true);
      }

      reloadFormView && reloadFormView();
    }
  };

  const cancel = () => {
    void navigate({ to: `/` });
  };

  return (
    <FormRenderer
      callback={callback}
      processId={params.processId}
      cancel={cancel}
      formData={data}
      type="task"
      isInitial={isInitialLoad}
    />
  );
}
